import type { Plan, PlanBillingPeriod, PlanBillingVariant } from '@/core/domain';
import {
  PLAN_BILLING_PERIOD_MONTHS,
  PLAN_BILLING_PERIODS,
  formatPlanPriceBrlFromCents,
  isFreePlan,
} from '@/core/domain';
import { CHOOSE_PLAN_PREMIUM_CARD_TITLE } from '@/features/plans/presentation/composables/constants/choose-plan-screen.constants';
import {
  computeEquivalentMonthlyCents,
  computeSavingsVsMonthlyStackedCents,
} from '@/features/plans/presentation/constants/plan-offers-display';

const PRICE_SUB: Record<PlanBillingPeriod, string> = {
  monthly: '/mês',
  semiannual: '/semestre',
  annual: '/ano',
};

const BILLING_NOTE: Record<PlanBillingPeriod, string> = {
  monthly: 'Cobrança todo mês',
  semiannual: 'Cobrança a cada 6 meses',
  annual: 'Cobrança uma vez por ano',
};

function sortPlansByOrder(a: Plan, b: Plan): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.title.localeCompare(b.title, 'pt-BR');
}

/** Referência premium + variante por período (checkout usa `planId` do documento pago). */
export interface ChoosePlanPremiumPeriodRef {
  planId: string;
  period: PlanBillingPeriod;
  variant: PlanBillingVariant;
}

export interface ChoosePlanDualLayoutModel {
  freePlan: Plan | null;
  paidPlan: Plan | null;
  premiumByPeriod: Partial<Record<PlanBillingPeriod, ChoosePlanPremiumPeriodRef>>;
  monthlyBaselineCents: number | null;
}

export function buildChoosePlanDualLayoutModel(plans: Plan[]): ChoosePlanDualLayoutModel {
  const active = plans.filter((p) => p.is_active);
  const freeCandidates = active.filter(isFreePlan).sort(sortPlansByOrder);
  const freePlan = freeCandidates[0] ?? null;

  const paidCandidates = active.filter((p) => p.kind === 'paid').sort(sortPlansByOrder);
  const paidPlan = paidCandidates[0] ?? null;

  const premiumByPeriod: Partial<Record<PlanBillingPeriod, ChoosePlanPremiumPeriodRef>> = {};
  if (paidPlan?.billing_variants) {
    for (const period of PLAN_BILLING_PERIODS) {
      const variant = paidPlan.billing_variants[period];
      if (variant) {
        premiumByPeriod[period] = { planId: paidPlan.id, period, variant };
      }
    }
  }

  const monthlyVariant = premiumByPeriod.monthly?.variant;
  const monthlyBaselineCents = monthlyVariant?.price_brl_cents ?? null;

  return { freePlan, paidPlan, premiumByPeriod, monthlyBaselineCents };
}

export function hasAnyPremiumPlan(model: ChoosePlanDualLayoutModel): boolean {
  return PLAN_BILLING_PERIODS.some((period) => model.premiumByPeriod[period] != null);
}

export interface ChoosePlanPremiumCardDisplay {
  planId: string;
  billingPeriod: PlanBillingPeriod;
  title: string;
  priceLine: string;
  priceSub: string;
  billingNote: string;
  equivalentMonthlyCents?: number;
  savingsTotalCents?: number;
}

export function buildPremiumPlanCardDisplay(
  ref: ChoosePlanPremiumPeriodRef,
  monthlyBaselineCents: number | null
): ChoosePlanPremiumCardDisplay {
  const { period, variant } = ref;
  const months = PLAN_BILLING_PERIOD_MONTHS[period];
  const totalCents = variant.price_brl_cents;

  let equivalentMonthlyCents: number | undefined;
  if (period !== 'monthly' && months > 0) {
    equivalentMonthlyCents = computeEquivalentMonthlyCents(totalCents, months);
  }

  let savingsTotalCents: number | undefined;
  if (period !== 'monthly' && monthlyBaselineCents != null && months > 0) {
    savingsTotalCents = computeSavingsVsMonthlyStackedCents(
      totalCents,
      months,
      monthlyBaselineCents
    );
  }

  return {
    planId: ref.planId,
    billingPeriod: period,
    title: CHOOSE_PLAN_PREMIUM_CARD_TITLE,
    priceLine: formatPlanPriceBrlFromCents(totalCents),
    priceSub: PRICE_SUB[period],
    billingNote: BILLING_NOTE[period],
    equivalentMonthlyCents,
    savingsTotalCents,
  };
}
